<script lang="ts">
  import type { Permission } from '@clinic/contracts';
  import {
    Activity,
    Boxes,
    CalendarDays,
    CalendarPlus,
    ChartNoAxesColumn,
    FileUp,
    MessageSquareText,
    PackageOpen,
    PackagePlus,
    Search,
    Settings,
    User,
    UserPlus,
    Users,
  } from '@lucide/svelte';
  import type { Component } from 'svelte';
  import { usePatients } from '../features/patients/api/patients-api';
  import { useOrg } from '../shared/auth/org.svelte';
  import { fa } from '../shared/i18n/fa';
  import { cn } from '../shared/lib/cn';
  import { debounced } from '../shared/lib/debounced.svelte';
  import { focusOnMount } from '../shared/lib/focus';
  import { personName, phoneLabel } from '../shared/lib/format';
  import { location, navigate } from '../shared/router/router.svelte';

  interface Entry {
    id: string;
    label: string;
    hint?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Icon: Component<any>;
    to: string;
    group: 'patients' | 'commands';
  }

  /**
   * Ctrl K / ⌘K (ADR-016 principle 5): find a patient or run a command from anywhere. It only
   * navigates; every page it opens enforces its own permission, and so does the server.
   */
  let { open, onClose }: { open: boolean; onClose: () => void } = $props();

  const org = useOrg();
  let dialog = $state<HTMLDialogElement>();
  let q = $state('');
  let active = $state(0);
  const search = debounced(() => q.trim(), 200);
  const canPatients = $derived(org.can('patients.read'));
  const patients = usePatients(
    org.orgId,
    () => search.value,
    false,
    6,
    () => canPatients && open && search.value !== '',
  );

  $effect(() => {
    const d = dialog;
    if (!d) return;
    if (open && !d.open) {
      q = '';
      active = 0;
      d.showModal();
    }
    if (!open && d.open) d.close();
  });

  const entries = $derived.by<Entry[]>(() => {
    const base = `/o/${org.orgId}`;
    const commands: (Entry & { permission?: Permission })[] = [
      { id: 'c-cal', label: fa.palette.go.calendar, Icon: CalendarDays, to: `${base}/calendar`, group: 'commands', permission: 'appointments.read' },
      {
        id: 'c-book',
        label: fa.palette.go.newAppointment,
        Icon: CalendarPlus,
        to: `${base}/calendar?book=1`,
        group: 'commands',
        permission: 'appointments.manage',
      },
      { id: 'c-pat', label: fa.palette.go.patients, Icon: Users, to: `${base}/patients`, group: 'commands', permission: 'patients.read' },
      {
        id: 'c-newpat',
        label: fa.palette.go.newPatient,
        Icon: UserPlus,
        to: `${base}/patients?new=1`,
        group: 'commands',
        permission: 'patients.write',
      },
      {
        id: 'c-import',
        label: fa.palette.go.importPatients,
        Icon: FileUp,
        to: `${base}/patients/import`,
        group: 'commands',
        permission: 'patients.import',
      },
      {
        id: 'c-export',
        label: fa.palette.go.exportData,
        Icon: PackageOpen,
        to: `${base}/settings?tab=data`,
        group: 'commands',
        permission: 'data.export',
      },
      { id: 'c-sms', label: fa.palette.go.sms, Icon: MessageSquareText, to: `${base}/sms`, group: 'commands', permission: 'sms.send' },
      {
        id: 'c-fin',
        label: fa.palette.go.finance,
        Icon: ChartNoAxesColumn,
        to: `${base}/finance`,
        group: 'commands',
        permission: 'finance.read',
      },
      { id: 'c-inv', label: fa.palette.go.inventory, Icon: Boxes, to: `${base}/inventory`, group: 'commands', permission: 'inventory.read' },
      {
        id: 'c-rcv',
        label: fa.palette.go.receive,
        Icon: PackagePlus,
        to: `${base}/inventory?tab=receive`,
        group: 'commands',
        permission: 'inventory.receive',
      },
      { id: 'c-set', label: fa.palette.go.settings, Icon: Settings, to: `${base}/settings`, group: 'commands', permission: 'settings.manage' },
      // The touch recorder (shared/diag), on the page the member is on; open to everyone, records no data.
      { id: 'c-diag', label: fa.palette.go.diagnostics, Icon: Activity, to: `${location.pathname}?diag=1`, group: 'commands' },
    ];
    const found: Entry[] =
      search.value && canPatients
        ? (patients.data?.pages[0]?.items ?? []).map((p) => ({
            id: `p-${p.id}`,
            label: personName(p),
            hint: phoneLabel(p.phone),
            Icon: User,
            to: `${base}/patients/${p.id}`,
            group: 'patients' as const,
          }))
        : [];
    const term = q.trim();
    return [...found, ...commands.filter((c) => (!c.permission || org.can(c.permission)) && (!term || c.label.includes(term)))];
  });

  const index = $derived(Math.min(active, Math.max(0, entries.length - 1)));
  function go(entry: Entry | undefined) {
    if (!entry) return;
    onClose();
    navigate(entry.to);
  }
</script>

<!-- svelte-ignore a11y_click_events_have_key_events -->
<dialog
  bind:this={dialog}
  onclose={() => {
    // Escape and the back button are left to the browser; `close` reports them.
    if (open && !dialog?.open) onClose();
  }}
  onclick={(e) => {
    if (e.target === e.currentTarget) onClose();
  }}
  aria-label={fa.palette.open}
  class="pop-enter m-auto mt-[12dvh] w-[min(94vw,560px)] rounded-xl border border-[var(--neu-edge)] bg-surface p-0 text-text shadow-raised-lg backdrop:bg-[var(--scrim)]"
>
  {#if open}
    <div>
      <div class="flex items-center gap-2 border-b border-hairline px-4">
        <Search class="size-4 shrink-0 text-text-4" aria-hidden="true" />
        <input
          use:focusOnMount={true}
          value={q}
          oninput={(e) => {
            q = e.currentTarget.value;
            active = 0;
          }}
          onkeydown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              active = (index + 1) % Math.max(1, entries.length);
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              active = (index - 1 + entries.length) % Math.max(1, entries.length);
            } else if (e.key === 'Enter') {
              e.preventDefault();
              go(entries[index]);
            }
          }}
          placeholder={fa.palette.placeholder}
          role="combobox"
          aria-expanded="true"
          aria-controls="palette-results"
          aria-activedescendant={entries[index] ? `palette-${entries[index].id}` : undefined}
          aria-label={fa.palette.open}
          class="min-h-13 w-full bg-transparent py-3.5 text-base placeholder:text-text-4 focus:outline-none"
        />
      </div>
      <ul id="palette-results" role="listbox" aria-label={fa.palette.open} class="max-h-[50dvh] overflow-y-auto p-2">
        {#if entries.length === 0}
          <li class="px-3 py-6 text-center text-sm text-text-3">{fa.palette.noResults}</li>
        {:else}
          {#each entries as entry, i (entry.id)}
            <!-- svelte-ignore a11y_click_events_have_key_events -->
            <li
              id={`palette-${entry.id}`}
              role="option"
              aria-selected={i === index}
              onmouseenter={() => (active = i)}
              onclick={() => go(entry)}
              class={cn(
                'flex min-h-11 cursor-pointer items-center gap-3 rounded-md px-3 text-sm',
                i === index ? 'bg-accent-soft text-accent' : 'text-text-2',
              )}
            >
              <entry.Icon class="size-4 shrink-0" aria-hidden="true" />
              <span class="min-w-0 flex-1 truncate">{entry.label}</span>
              {#if entry.hint}
                <bdi dir="ltr" class="text-xs text-text-3">{entry.hint}</bdi>
              {:else}
                <span class="text-xs text-text-4">{fa.palette[entry.group]}</span>
              {/if}
            </li>
          {/each}
        {/if}
      </ul>
    </div>
  {/if}
</dialog>
